/**
 * AI Fundamentals pattern definitions.
 *
 * Each pattern describes one core AI concept relevant to DevOps.
 * `icon` must match a key in components/Icons.jsx.
 */

const patterns = [
  {
    id: 'prompts',
    title: 'Prompts & Prompt Engineering',
    subtitle: 'The language of AI communication',
    icon: 'Prompt',
    color: '#38bdf8',
    description:
      'Prompts are the primary interface between humans and AI models. In DevOps, prompt engineering transforms vague requests into precise, actionable instructions that produce consistent, reliable outputs.',
    why: "Every AI-powered DevOps tool starts with a prompt. Whether it's generating Terraform modules, analyzing logs, or reviewing pull requests — the quality of the prompt determines the quality of the output. A well-engineered prompt is the difference between a useful tool and a hallucinating one.",
    principles: [
      { name: 'Role Definition', desc: "Give the AI a specific expert persona: 'You are a senior SRE analyzing infrastructure drift between Terraform state and actual cloud resources.' This activates relevant knowledge patterns." },
      { name: 'Structured Context', desc: 'Provide all necessary data in a clear, parseable format. Use template variables ({{environment}}, {{cloud}}) that get filled at runtime.' },
      { name: 'Output Schema', desc: 'Define the exact JSON structure you expect back. This constrains the model and makes responses machine-parseable.' },
      { name: 'Few-Shot Examples', desc: 'Show the model what good output looks like. Include 1-2 examples of input→output pairs in the prompt.' },
      { name: 'Chain of Thought', desc: "Ask the model to reason step-by-step before giving the answer. 'First analyze X, then evaluate Y, finally recommend Z.'" },
    ],
    devopsExample: `# Infrastructure Drift Detection Prompt

You are an expert SRE analyzing infrastructure drift.

## Context
- Environment: {{environment}}
- Provider: {{cloud}}

## State Resources
\`\`\`json
{{state_resources}}
\`\`\`

## Task
Compare state against cloud resources.
For each drift: identify what changed,
assess severity, hypothesize root cause.

## Output Format (JSON)
{
  "drifted_resources": [...],
  "security_drift_count": 0
}`,
  },

  {
    id: 'agents',
    title: 'Agents & Skills',
    subtitle: 'Autonomous workers with specialized abilities',
    icon: 'Agent',
    color: '#a78bfa',
    description:
      'An AI Agent is an autonomous entity that perceives its environment, makes decisions, and takes actions to achieve goals. Skills are the specific capabilities an agent possesses — discrete, testable units of work.',
    why: "DevOps automation has always been about agents: monitoring agents, deployment agents, configuration agents. AI agents are the next evolution — they don't just execute predefined scripts, they reason about context and adapt. Skills make agents composable and testable.",
    principles: [
      { name: 'Single Responsibility', desc: 'Each agent owns one domain (security, cost, drift). Each skill does one thing (scan_security, estimate_cost, detect_drift). One skill = one LLM call.' },
      { name: 'Typed I/O', desc: 'Every skill has a Pydantic Input and Output schema. No ambiguous data passing. The contract is explicit and validated at runtime.' },
      { name: 'Autonomy Levels', desc: 'Agents operate at different autonomy levels: Suggest (recommend actions), Act (execute with approval), Auto (execute autonomously). Environment determines level.' },
      { name: 'Context Awareness', desc: 'Agents receive a shared Context object with all relevant state — environment, cloud, repo, previous agent results. No skill operates in a vacuum.' },
      { name: 'Guardrailed Execution', desc: 'Every agent action passes through guardrails: policy checks, rate limits, kill switches, approval gates. Autonomy is earned, not assumed.' },
    ],
    devopsExample: `class DetectDriftSkill(BaseSkill):
    """One skill = one LLM call"""

    async def execute(self, input):
        # 1. Load prompt template
        prompt = load_prompt(
            "drift_watcher",
            "detect_drift",
            {"environment": input.environment}
        )
        # 2. Call LLM
        result = await call_llm(
            prompt,
            DetectDriftOutput
        )
        # 3. Return validated output
        return result`,
  },

  {
    id: 'mcp',
    title: 'MCP (Model Context Protocol)',
    subtitle: 'The universal adapter for AI tools',
    icon: 'MCP',
    color: '#34d399',
    description:
      'MCP is a standard protocol that lets AI models connect to external tools and data sources. Think of it as USB-C for AI — one standard interface to connect to any service: GitHub, Terraform Cloud, AWS, Prometheus, Slack.',
    why: "Without MCP, every AI integration is a custom REST client with custom auth, custom error handling, custom parsing. MCP standardizes this: define your tools once, connect any AI model. It's the difference between writing 50 custom integrations and writing 50 MCP servers that any model can use.",
    principles: [
      { name: 'Tool Definition', desc: 'Each MCP server exposes tools with typed parameters and descriptions. The AI model discovers available tools and decides when to use them.' },
      { name: 'Resource Exposure', desc: 'MCP servers can expose resources (files, database records, API responses) that the model can read for context.' },
      { name: 'Standardized Transport', desc: 'HTTP/SSE or stdio transport. Any language, any framework. The protocol handles serialization, error codes, and streaming.' },
      { name: 'Composability', desc: 'Multiple MCP servers can be connected simultaneously. An agent can use GitHub + Terraform Cloud + AWS in a single workflow.' },
      { name: 'Security Boundary', desc: 'MCP servers handle authentication, rate limiting, and access control. The AI model never sees raw credentials.' },
    ],
    devopsExample: `# MCP Server: Terraform Cloud
# Agent calls tools through standard protocol

tools:
  - name: get_current_state
    description: Get Terraform state for workspace
    params: { workspace_id: string }
    returns: { state_json: object }

  - name: trigger_run
    description: Start a Terraform plan/apply
    params:
      workspace_id: string
      auto_apply: boolean
    returns: { run_id: string }

  - name: get_plan_json
    description: Get plan output as JSON
    params: { run_id: string }
    returns: { plan: object }`,
  },

  {
    id: 'rag',
    title: 'RAG (Retrieval-Augmented Generation)',
    subtitle: 'Ground AI in your actual data',
    icon: 'RAG',
    color: '#fb923c',
    description:
      "RAG combines a retrieval system (search over your documents, runbooks, incident history) with an AI generator. Instead of relying on the model's training data, RAG lets you inject your organization's specific knowledge at inference time.",
    why: "AI models don't know your infrastructure. They don't know that your payment-service has a P95 latency SLO of 200ms, or that your last three outages were caused by cascading timeouts. RAG bridges this gap — it retrieves your specific context and feeds it to the model alongside the question.",
    principles: [
      { name: 'Knowledge Base', desc: 'A structured collection of documents: incident postmortems, runbooks, architecture docs, golden configs, SLO definitions. Chunked, embedded, and indexed for semantic search.' },
      { name: 'Embedding & Indexing', desc: 'Documents are converted to vector embeddings and stored in a vector database. Queries find semantically similar content, not just keyword matches.' },
      { name: 'Contextual Retrieval', desc: 'Before the LLM generates a response, retrieve the top-K most relevant chunks and inject them into the prompt as context.' },
      { name: 'Freshness', desc: 'Knowledge bases must be kept current. Stale runbooks produce stale advice. Automate ingestion from your wiki, Git repos, and incident tools.' },
      { name: 'Relevance Filtering', desc: 'Not all retrieved documents are useful. Score relevance, filter low-quality matches, and limit context window usage to the most impactful chunks.' },
    ],
    devopsExample: `# Knowledge Base Structure

knowledge-base/
├── incidents/
│   └── patterns/
│       └── cascading_timeout.yaml
│           # symptoms, detection, prevention
├── alerts/
│   └── noise_patterns/
│       └── transient_cpu_spikes.yaml
│           # noise ratio: 85%
│           # solution: increase for_duration
├── performance/
│   └── baselines/
│       └── http_service.yaml
│           # critical: p95 < 200ms
│           # standard: p95 < 500ms
└── cost/
    └── baselines/
        └── grafana_cloud.yaml
            # metrics: $8/1k series`,
  },

  {
    id: 'multi-agent',
    title: 'Multi-Agent Systems',
    subtitle: 'Orchestrated collaboration between specialists',
    icon: 'MultiAgent',
    color: '#f472b6',
    description:
      'A Multi-Agent System is a group of specialized AI agents that collaborate to solve complex problems no single agent could handle alone. Each agent has deep expertise in one domain, and an orchestrator coordinates their work.',
    why: "DevOps problems are inherently multi-domain. A single PR might need: security scanning (Policy Guardian), cost estimation (Cost Sentinel), blast radius analysis (Change Planner), and code review (Module Architect). Multi-agent systems mirror how real DevOps teams work — specialists collaborating under coordination.",
    principles: [
      { name: 'Specialization', desc: 'Each agent is an expert in one domain. The Drift Watcher knows nothing about costs. The Cost Sentinel knows nothing about security. Deep expertise beats shallow generalism.' },
      { name: 'Orchestration', desc: "A workflow orchestrator coordinates agent sequence: sequential, parallel, conditional. 'If security scan finds critical issues → skip cost estimation → send to remediation.'" },
      { name: 'Shared Context', desc: 'All agents in a workflow share a Context object. Each agent reads previous results and adds its own. The Context is the single source of truth for the workflow.' },
      { name: 'Event-Driven Communication', desc: "Agents communicate through an event bus. Drift Watcher emits 'drift.detected' → State Doctor listens and reacts. Loose coupling enables composition." },
      { name: 'Maturity Stages', desc: 'Teams evolve: Manual invocation → Single agents in CI → Multi-agent workflows → Autonomous operation. Each stage builds trust.' },
    ],
    devopsExample: `# Workflow: PR Review Pipeline

Engineer opens PR with .tf changes
        │
        ▼
  ┌─ PARALLEL ─────────────┐
  │ Policy Guardian         │
  │  • scan_security        │
  │  • evaluate_policy      │
  │                         │
  │ Cost Sentinel           │
  │  • estimate_cost        │
  │  • enforce_budget       │
  └──────────┬─────────────┘
             │
             ▼
  Change Planner
   • analyze_plan
   • assess_risk
   • route_approval
             │
             ▼
  PR Comment with unified report`,
  },

  {
    id: 'guardrails',
    title: 'Guardrails & Safety',
    subtitle: 'Trust boundaries for autonomous AI',
    icon: 'Guardrails',
    color: '#34d399',
    description:
      'Guardrails are the policies, constraints, and safety mechanisms that govern what AI agents can and cannot do. They ensure that autonomous systems operate within defined boundaries and escalate to humans when uncertainty is high.',
    why: "Autonomous AI in production infrastructure is powerful — and dangerous. An agent that can modify Terraform state can also destroy it. Guardrails are non-negotiable: they define the blast radius of automation and ensure that humans remain in the loop for high-risk decisions.",
    principles: [
      { name: 'Autonomy Levels', desc: 'Define per-agent, per-environment autonomy. Dev environment: agents can auto-apply. Production: agents can only suggest. The environment tier determines the leash length.' },
      { name: 'Kill Switches', desc: "Emergency shutdown controls for every agent. If an agent starts producing unexpected outputs, kill it instantly. No graceful degradation — hard stop." },
      { name: 'Approval Gates', desc: 'Certain actions always require human approval: production state changes, SLO modifications, security group changes, budget overrides. No exceptions.' },
      { name: 'Rate Limits', desc: "Limit how many actions an agent can take per time window. Prevent runaway loops: an agent that triggers itself 1000 times is a bug, not a feature." },
      { name: 'Escalation Rules', desc: 'Define when agents must alert humans: P1 incidents, low confidence scores, error budget exhaustion, novel situations. When in doubt, escalate.' },
    ],
    devopsExample: `# policies/autonomy_levels.yaml

agents:
  drift_watcher:
    dev: autonomous    # auto-fix cosmetic drift
    staging: suggest   # suggest fixes, human applies
    prod: notify       # alert only, never touch

  cost_sentinel:
    dev: autonomous    # auto-tag, auto-right-size
    staging: suggest
    prod: suggest

# policies/approval_gates.yaml

always_require_approval:
  - production state modifications
  - security group changes
  - SLO target changes
  - budget limit overrides
  - environment teardown (staging+)`,
  },
];

export default patterns;
